import {
  type CallhomeSiteWithCameras,
  type DeviceEntity,
  type SiteId,
} from '@solinkcorp/api-types';
import { useStaleSWRConfig } from '@solinkcorp/utils/hooks';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import React from 'react';
import useSWR from 'swr';
import { type CallhomeCamera } from 'utils/CameraUtil';
import useCallhomeClient from '../useCallhomeClient';

/**
 * @experimental
 * To be used in the future. Refer to this epic for more details: https://solink.atlassian.net/browse/SL-3140
 */
export default function useCamerasFromSites(siteIds: SiteId[] = []) {
  const callhomeClient = useCallhomeClient();
  const shouldFetch = siteIds.length > 0;
  const camerasFromSitesKey = shouldFetch ? ['cameras', siteIds] : null;
  const staleSWRConfig = useStaleSWRConfig(camerasFromSitesKey);

  const res = useSWR(
    camerasFromSitesKey,
    async () => {
      try {
        const response = await callhomeClient.querySites<CallhomeSiteWithCameras>({
          filter: {
            where: { id: { inq: siteIds } },
            include: [
              {
                relation: 'cameras',
              },
            ],
          },
        });

        return response.results;
      } catch (_error) {
        throw new Error('Failed to fetch cameras');
      }
    },
    staleSWRConfig,
  );

  const sites = React.useMemo(() => res.data ?? [], [res.data]);

  const cameras: CallhomeCamera[] = React.useMemo(
    () => sites.flatMap((site) => site.cameras ?? []),
    [sites],
  );

  const camerasById = React.useMemo(() => keyBy(cameras, 'id'), [cameras]);

  const camerasBySiteId = React.useMemo(() => groupBy(cameras, 'siteId'), [cameras]);

  return React.useMemo(
    () => ({ cameras, camerasById, camerasBySiteId }),
    [cameras, camerasById, camerasBySiteId],
  );
}

export function getSiteCameras(siteIds: SiteId[], camerasBySiteId: Record<string, DeviceEntity[]>) {
  return siteIds.flatMap((siteId) => camerasBySiteId[siteId]?.map((device) => device) ?? []);
}

export function getSiteDeviceIds(
  siteIds: SiteId[],
  camerasBySiteId: Record<SiteId, DeviceEntity[]>,
) {
  return siteIds.flatMap((siteId) => camerasBySiteId[siteId]?.map((device) => device.id) ?? []);
}
